import React from "react";
import {
  Button,
  ButtonContainer,
  ContentSection,
  Fieldset,
  Form,
  Layout,
  Paragraph,
  SectionHeading,
} from "@vwfs-bronson/bronson-react";
import {
  SelectField,
  TextField,
  renderRadioGroup,
  Conditional,
  FieldDependent,
} from "../../formik";
import { Formik, FormikHelpers as FormikActions, FastField, Field } from "formik";
import { Output } from "../../Text";
import toOptions from "../../functional/toOptions";
import {
  showContractType,
  showEmployedSince,
} from "../../functional/employment";
import { resetField } from "../../formik/resetField";
import { DateField } from "../../formik/DateField";
import { normalizeEuroAmount } from "../../functional/normalize";
import * as Validation from "../../validation";
import { ProductModifier } from "@busdk/storefront-service/dist/models/enums";
import { Route } from "../../common/Navigation";
import { CountryCode } from "@busdk/credit-decision-service/dist/client/src/models/enums";
import * as H from "history";
import { ErrorCode } from "../../common/ErrorInformation";
import { CreditCheckPopup } from "../../components/CreditCheckPopup/CreditCheckPopup";
import {
  performFormbasedSoftCreditCheck,
  redirectBasedOnCreditCheckResult,
} from "../../services/api/softCreditCheck";
import { Analytics } from '../../services/analytics';
import { setActualPage, trackAbort, trackFirstCustomerInteraction } from '../page.utils';
import { alphabeticallyValueCompare } from '../../utils';
import { defaultSelectValue } from '../../common/types/common';

const select = (whitelist: any, all: any) =>
  all.filter((v: any) => whitelist.includes(v.key));

const currencyAddon = {
  addonText: Output.resolve("generic.currencySymbol"),
};

export type AdditionalAppFormDataType = {
  professionGroup: string;
  employment: "limited" | "unlimited";
  employmentLimit: string;
  netIncome: number;
  monthlyExpenses: number;
  storefrontTransactionId: string;
  nationality: string;
  country: CountryCode;
};

class AdditionalAppFormPage extends React.Component<
  AdditionalAppFormPageProps,
  any
> {
  pos: any;

  orderedProfessionGroups = Output.resolve(
    "pages.applicationForm.panels.employmentData.fields.professionGroup.values",
    []
  )?.sort(alphabeticallyValueCompare);

  constructor(props: AdditionalAppFormPageProps) {
    super(props);

    this.state = {
      initialValues: {
        ...props.initialValues,
        isCreditCheckInProgress: false,
      },
      changed: false,
      lastTouchedField: undefined,
      errors: []
    };
  }

  pageName = 'Bank account (manual)'

  defaultTrackingData = {
    [Analytics.keys.pageName()]: this.pageName,
    [Analytics.keys.viewChange()]: this.pageName,
    [Analytics.keys.formType()]: 'Bank data',
    [Analytics.keys.formName()]: 'Bank account'
  }

  trackPage = () => {
    setTimeout(() => {
      const pageData = {
        [Analytics.keys.eventType(0)]: 'pageView',
        [Analytics.keys.eventAction(0)]: 'Success',
      }
      Analytics.setTrackingData({...this.defaultTrackingData, ...pageData})
      Analytics.trackPage()
    }, 1000)
  }

  trackSuccess = () => {
    const successData = {
      [Analytics.keys.eventType(0)]: 'interAction',
      [Analytics.keys.eventAction(0)]: 'Success',
      [Analytics.keys.linkInformation(0)]: 'Personal data (DAC) provided',
    }
    Analytics.setTrackingData({...this.defaultTrackingData, ...successData})
    Analytics.trackInteraction()
  }

  onBlur = (ev: any) => {
    if (ev && ev.target && ev.target.name) {
      this.setState({
        lastTouchedField: ev.target.name.split('.')[ev.target.name.split('.').length - 1]
      })
      if (!this.state.changed) {
        this.setState({changed: true})
        trackFirstCustomerInteraction(this.defaultTrackingData)
      }
    }
  }

  onUnload = () => {
    trackAbort(this.state.lastTouchedField, this.defaultTrackingData)
  }

  ownUnload = () => {
    window.addEventListener('unload', this.onUnload)
  }

  desubscribeUnload = () => {
    window.removeEventListener('unload', this.onUnload)
  }

  componentDidMount() {
    setActualPage(this.pageName)
    window.scrollTo(0, 0);
    this.trackPage()
    this.ownUnload()
  }

  componentWillUnmount() {
    this.desubscribeUnload()
  }

  validator = Validation.select(
    "professionGroup",
    "employment",
    "employmentLimit",
    "netIncome",
    "monthlyExpenses"
  );

  async handleSubmit(
    values: AdditionalAppFormDataType,
    formikActions: FormikActions<any>
  ) {
    try {
      if (this.state.isCreditCheckInProgress) {
        return;
      }
      this.setState({ isCreditCheckInProgress: true });
      const creditCheckResult = await performFormbasedSoftCreditCheck({
        transactionId: values.storefrontTransactionId,
        formData: values,
        productmodifier: this.props.productModifier,
      });
      this.setState({
        isCreditCheckApproved: creditCheckResult.isEligible,
      });
      if (this.props.onSectionSubmit) {
        this.props.onSectionSubmit({ values });
      }
      // redirect to approve or refused page depending on credit check result
      redirectBasedOnCreditCheckResult(this.props.history, creditCheckResult);
    } catch (e) {
      this.setState({ error: true }); // todo what else to do in error case here?
      this.props.history.push(Route.Error, {
        errorCode: ErrorCode.AdditionalAppFormSubmit,
      });
    } finally {
      this.setState({ isCreditCheckInProgress: false });
    }
  }

  render() {
    return (
      <ContentSection pageWrap pageWrapSize="medium">
        <div className="center-header">
        <SectionHeading
          primary
          level={"1"}
          subtitle={Output.resolve("pages.leasing.additionalAppForm.subtitle")}
        >
          {Output.resolve(
            this.props.dacFailed
              ? "pages.leasing.additionalAppForm.titleErrorCase"
              : "pages.leasing.additionalAppForm.title"
          )}

        </SectionHeading>
        </div>
        <div className="o-component-wrapper">
          <Paragraph testId="second-heading">
            <strong>
              {Output.resolve(
                "pages.leasing.additionalAppForm.additionalDataHeader.title"
              )}
            </strong>
          </Paragraph>
          <Formik
            initialValues={this.state.initialValues}
            validate={(values) => {
              const result = this.validator(values);
              return result;
            }}
            enableReinitialize={false}
            onSubmit={(
              values: AdditionalAppFormDataType,
              actions: FormikActions<AdditionalAppFormDataType>
            ) => this.handleSubmit(values, actions)}
            render={({
              values,
              handleSubmit,
            }: {
              values: any;
              handleSubmit: any;
            }) => (
              <Form onSubmit={handleSubmit} onBlur={this.onBlur} floatingLabel>
                <ContentSection.ComponentWrapper>
                  <Fieldset>
                    <Fieldset.Row>
                      <Layout>
                        <Layout.Item default={"1/2"} m={"1/2"} xs={"1/1"}>
                          <Field
                            name="professionGroup"
                            label={Output.resolve(
                              "pages.applicationForm.panels.employmentData.fields.professionGroup.label"
                            )}
                            component={SelectField}
                          >
                            {toOptions(
                              [
                                defaultSelectValue,
                                ...this.orderedProfessionGroups,
                              ]
                            )}
                          </Field>
                        </Layout.Item>
                      </Layout>
                    </Fieldset.Row>
                    {showContractType(values.professionGroup) && (
                      <Fieldset.Row>
                        <Layout>
                          <Layout.Item default={"1/2"} m={"1/2"} xs={"1/1"}>
                            {renderRadioGroup({
                              fieldName: "employment",
                              legend: Output.resolve(
                                "pages.applicationForm.panels.employmentData.fields.employment.label"
                              ),
                              options: select(
                                ["limited", "unlimited"],
                                Output.resolve(
                                  "pages.applicationForm.panels.employmentData.fields.employment.values"
                                )
                              ),
                              tooltip: undefined,
                            })}
                          </Layout.Item>
                        </Layout>
                      </Fieldset.Row>
                    )}
                    {showEmployedSince(values.professionGroup) && (
                      <Fieldset.Row>
                        <Layout>
                          <Layout.Item default={"1/2"} m={"1/2"} xs={"1/1"}>
                            <FieldDependent
                              names={["employment", "professionGroup"]}
                              render={({
                                values: { employment, professionGroup },
                                formik,
                              }: {
                                values: {
                                  employment: any;
                                  professionGroup: any;
                                };
                                formik: any;
                              }) => (
                                <Conditional
                                  collapsed={
                                    employment !== "limited" &&
                                    professionGroup !== "APPRENTICE"
                                  }
                                  onCollapse={() => {
                                    resetField("employmentLimit", formik);
                                  }}
                                >
                                  <FastField
                                    name="employmentLimit"
                                    label={Output.resolve(
                                      "pages.applicationForm.panels.employmentData.fields.employmentPeriod.label"
                                    )}
                                    component={DateField}
                                  />
                                </Conditional>
                              )}
                            />
                          </Layout.Item>
                        </Layout>
                      </Fieldset.Row>
                    )}
                    <Fieldset.Row>
                      <Layout>
                        <Layout.Item default="1/2" m="1/2" xs="1/1">
                          <FastField
                            name="netIncome"
                            label={Output.resolve(
                              "pages.leasing.additionalAppForm.netIncome.label"
                            )}
                            tooltip={Output.resolveAsMarkdown(
                              "pages.leasing.additionalAppForm.netIncome.toolTip"
                            )}
                            normalizeOnChange={false}
                            normalize={normalizeEuroAmount}
                            reversed
                            {...currencyAddon}
                            component={TextField}
                          />
                        </Layout.Item>
                        <Layout.Item default="1/2" m="1/2" xs="1/1">
                          <FastField
                            name="monthlyExpenses"
                            normalizeOnChange={false}
                            normalize={normalizeEuroAmount}
                            reversed
                            {...currencyAddon}
                            label={Output.resolve(
                              "pages.leasing.additionalAppForm.netExpenses.label"
                            )}
                            tooltip={Output.resolveAsMarkdown(
                              "pages.leasing.additionalAppForm.netExpenses.toolTip"
                            )}
                            component={TextField}
                          />
                        </Layout.Item>
                      </Layout>
                    </Fieldset.Row>
                  </Fieldset>
                </ContentSection.ComponentWrapper>
                <ContentSection.ComponentWrapper>
                  <ButtonContainer right>
                    <Button
                      type="submit"
                      small
                      testId="additionalAppForm-global-CTA"
                    >
                      {Output.resolve("pages.leasing.additionalAppForm.cta")}
                    </Button>
                  </ButtonContainer>
                </ContentSection.ComponentWrapper>
              </Form>
            )}
          />
        </div>
        <CreditCheckPopup show={this.state.isCreditCheckInProgress} />
      </ContentSection>
    );
  }
}

type AdditionalAppFormPageProps = {
  initialValues: any;
  productModifier: ProductModifier.NEW | ProductModifier.USED;
  history: H.History;
  dacFailed: boolean;
  onSectionSubmit: Function;
};

export { AdditionalAppFormPage };
